import React from 'react';
import PropTypes from 'prop-types';
import Table from 'antd/lib/table';
import Button from 'antd/lib/button';
import Badge from 'antd/lib/badge';
import Modal from 'antd/lib/modal';
import Tag from 'antd/lib/tag';
import get from 'lodash/get';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { showErrorNotification, showInfoNotification } from 'components';
import { createLink } from 'utils';
import formatRelativeDate from 'utils/formatRelativeDate';

import {
  destroyMap,
  fetchMaps,
  datasetDestroy,
  fetchDatasets,
} from 'graphql/queries';
import { useMutation } from '@apollo/react-hooks';

const { confirm } = Modal;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const DatasetList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DangerAction = styled(Button)`
  color: #dc3545;

  &:hover {
    color: #a71d2a;
  }
`;

const queryByType = {
  Map: {
    destroyMutation: destroyMap,
    fetchQuery: fetchMaps,
    deletedIdPath: 'mapDestroy.deletedId',
  },
  Dataset: {
    destroyMutation: datasetDestroy,
    fetchQuery: fetchDatasets,
    deletedIdPath: 'datasetDestroy.deletedId',
  },
};

const sortByText = (left, right, key) => {
  const leftValue = get(left, key, '').toLowerCase();
  const rightValue = get(right, key, '').toLowerCase();

  return leftValue.localeCompare(rightValue);
};

const sortByDate = (left, right, key) => {
  const leftValue = Date.parse(get(left, key, '')) || 0;
  const rightValue = Date.parse(get(right, key, '')) || 0;

  return leftValue - rightValue;
};

const getLocationsLabel = (record) => {
  const totalCount = get(record, 'datapoints.totalCount', null);

  if (totalCount === null) {
    return '—';
  }

  return totalCount > 0 ? `${totalCount} locations` : 'Empty';
};

const shapesToShowCount = 5;

const normalizeMapShapes = (mapShapes) => {
  const shapesByGroup = mapShapes.reduce((accumulator, mapShape) => {
    const shapeGroup = get(mapShape, 'shape.shapeGroup', null);

    if (!shapeGroup || !shapeGroup.id) {
      accumulator.standalone.push(mapShape);
      return accumulator;
    }

    if (!accumulator.grouped[shapeGroup.id]) {
      accumulator.grouped[shapeGroup.id] = {
        shapeGroup,
        mapShapes: [],
      };
    }

    accumulator.grouped[shapeGroup.id].mapShapes.push(mapShape);

    return accumulator;
  }, { grouped: {}, standalone: [] });

  const collapsedGroupShapes = Object.values(shapesByGroup.grouped).reduce((
    accumulator,
    { shapeGroup, mapShapes: groupedMapShapes },
  ) => {
    const groupShapeIds = get(shapeGroup, 'shapes', []).map(shape => shape.id);
    const includedShapeIds = groupedMapShapes
      .map(mapShape => get(mapShape, 'shape.id', null))
      .filter(Boolean);
    const includesWholeGroup = groupShapeIds.length > 0
      && groupShapeIds.length === includedShapeIds.length
      && groupShapeIds.every(id => includedShapeIds.includes(id));

    if (includesWholeGroup) {
      accumulator.push({
        id: `group-${shapeGroup.id}`,
        name: shapeGroup.name,
      });
      return accumulator;
    }

    groupedMapShapes.forEach(({ id, shape }) => accumulator.push({
      id,
      name: get(shape, 'name', 'Unnamed boundary'),
    }));
    return accumulator;
  }, []);

  return [
    ...collapsedGroupShapes,
    ...shapesByGroup.standalone.map(({ id, shape }) => ({
      id,
      name: get(shape, 'name', 'Unnamed boundary'),
    })),
  ];
};

const renderBoundaries = (_, record) => {
  const mapShapes = get(record, 'mapShapes', []);

  if (!mapShapes.length) {
    return '—';
  }

  const normalizedShapes = normalizeMapShapes(mapShapes);
  const visibleShapes = normalizedShapes.slice(0, shapesToShowCount);
  const remainingShapeCount = normalizedShapes.length - visibleShapes.length;

  return (
    <TagList>
      {visibleShapes.map(({ id, name }) => (
        <Tag color="#6c757d" key={id}>
          {name}
        </Tag>
      ))}
      {remainingShapeCount > 0 && <span>{`+ ${remainingShapeCount} more`}</span>}
    </TagList>
  );
};

const renderDatasets = (_, record) => {
  const mapDatasets = get(record, 'mapDatasets', []);

  if (!mapDatasets.length) {
    return '—';
  }

  return (
    <DatasetList>
      {mapDatasets.map(({ id, name, color }) => (
        <div key={id}>
          <Badge color={color} text={name} />
        </div>
      ))}
    </DatasetList>
  );
};

const TableActions = ({ id, name, __typename }) => {
  const config = queryByType[__typename];

  const [runMutation] = useMutation(config.destroyMutation, {
    variables: {
      input: {
        id,
      },
    },
    refetchQueries: [{ query: config.fetchQuery }],
    onCompleted: (response) => {
      const deletedId = get(response, config.deletedIdPath, false);

      if (deletedId) {
        showInfoNotification(`Deleted ${__typename.toLowerCase()} with id ${deletedId}`);
      }
    },
    onError: (error) => {
      const deletedError = get(error, 'graphQLErrors[0].message', false);

      if (deletedError) {
        showErrorNotification(deletedError);
      }
    },
  });

  const handleDelete = () => confirm({
    title: `Are you sure you want to delete ${name}?`,
    okButtonProps: { id: 'confirm-card-delete' },
    onOk() {
      runMutation();
    },
  });

  return (
    <Actions>
      <Button type="link">
        <Link to={createLink('edit', id)}>Open</Link>
      </Button>
      <DangerAction id="delete-card" onClick={handleDelete} type="link">
        Delete
      </DangerAction>
    </Actions>
  );
};

TableActions.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  __typename: PropTypes.string.isRequired,
};

const baseColumns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: (left, right) => sortByText(left, right, 'name'),
    defaultSortOrder: 'ascend',
    render: (value, record) => <Link to={createLink('edit', record.id)}>{value}</Link>,
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    render: value => value || '—',
  },
  {
    title: 'Locations',
    key: 'locations',
    render: (_, record) => getLocationsLabel(record),
  },
  {
    title: 'Last Updated',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    sorter: (left, right) => sortByDate(left, right, 'updatedAt'),
    render: value => formatRelativeDate(value),
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (_, record) => <TableActions {...record} />,
  },
];

const mapOnlyColumns = [
  {
    title: 'Boundaries',
    key: 'boundaries',
    render: renderBoundaries,
  },
  {
    title: 'Datasets',
    key: 'datasets',
    render: renderDatasets,
  },
];

const datasetOnlyColumns = [
  baseColumns[2],
];

const Cards = ({ cards, type }) => (
  <Table
    columns={type === 'Map' ? [
      ...baseColumns.slice(0, 2),
      ...mapOnlyColumns,
      ...baseColumns.slice(3),
    ] : [
      ...baseColumns.slice(0, 2),
      ...datasetOnlyColumns,
      ...baseColumns.slice(3),
    ]}
    dataSource={cards}
    id="cards"
    pagination={false}
    rowKey="id"
  />
);

Cards.propTypes = {
  cards: PropTypes.array.isRequired,
  type: PropTypes.string.isRequired,
};

export default Cards;
